import { useCallback, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { FeatureStatusBadge } from "@/components/FeatureStatusBadge";
import { List } from "@/components/List";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { useServerTable } from "@/hooks/useServerTable";
import { useTableFilters } from "@/hooks/useTableFilters";
import { alarm as alarmApi, type AlarmListFilters } from "@/endpoints/dryp/alarm";
import { CUSTOM_FILTERS } from "@/config/list";
import { getTableSearchParam } from "@/utils/filters";
import { useStore } from "@/hooks/useStore";
import { AlarmTypeFilter } from "./components/AlarmTypeFilter";
import { AlarmActiveFilter } from "./components/AlarmActiveFilter";
import { OwnAlarmsToggle } from "./components/OwnAlarmsToggle";
import { getAlarmsColumns } from "./AlarmsColumns";
import type { Alarm } from "./types";

const getAlarmListFilters = (
	tableFilters: { id: string; value: unknown }[] | undefined,
	currentUserId: string | undefined,
): AlarmListFilters => {
	const alarmTypes = tableFilters?.find(f => f.id === CUSTOM_FILTERS.alarmType)?.value as
		| Dryp.AlarmType[]
		| undefined;
	const enabled = tableFilters?.find(f => f.id === CUSTOM_FILTERS.enabled)?.value as boolean | undefined;
	const ownAlarms = tableFilters?.find(f => f.id === CUSTOM_FILTERS.ownAlarms)?.value === true;

	return {
		search: getTableSearchParam(tableFilters),
		alarmTypes,
		enabled,
		userIds: ownAlarms && currentUserId ? [currentUserId] : undefined,
	};
};

export const Alarms = observer(() => {
	const alarmsApi = useAsyncStateStore(alarmApi.getAll, { debounce: 300 });
	const {
		userStore,
		uiStore: { modalStore, sheetStore },
	} = useStore();
	const currentUserId = userStore.getUser?.id;
	const canManageMembers = userStore.isAdmin || userStore.isSuperAdmin;

	const alarms = alarmsApi.data?.items ?? [];

	// Re-fetches the list with the same params last used (page/sort/filters preserved).
	// `refresh` flips the list-wide loading flag; `silentRefresh` doesn't (used for
	// row-level mutations like a toggle, where the row owns its own loading state).
	// Both await the underlying fetch so callers can show a loader until data is in.
	const refresh = useCallback(async () => {
		await alarmsApi.refetch();
	}, [alarmsApi]);
	const silentRefresh = useCallback(async () => {
		await alarmsApi.silentFetch();
	}, [alarmsApi]);

	// ── Handlers ─────────────────────────────────────────────────

	const handleDelete = useCallback(
		(alarm: Alarm) => {
			modalStore.open("confirm", {
				title: "Delete alarm",
				description: `Permanently delete "${alarm.name}"? This cannot be undone.`,
				confirmButtonText: "Delete",
				onConfirmCallback: async () => {
					try {
						await alarmApi.delete(Number(alarm.id));
						modalStore.close();
						toast.success(`Alarm "${alarm.name}" deleted`);
						refresh();
					} catch (error) {
						modalStore.close();
						const apiError = error as ApiError;
						toast.error(apiError?.message || `Failed to delete alarm "${alarm.name}". Please try again.`);
					}
				},
			});
		},
		[modalStore, refresh],
	);

	const handleViewLog = useCallback(
		(alarm: Alarm) => {
			sheetStore.open("alarmDetail", { alarm });
		},
		[sheetStore],
	);

	const handleUnsubscribe = useCallback(
		(alarm: Alarm) => {
			if (!currentUserId) return;
			modalStore.open("confirm", {
				title: "Unsubscribe",
				description: `Stop receiving notifications for "${alarm.name}"?`,
				confirmButtonText: "Unsubscribe",
				onConfirmCallback: async () => {
					try {
						await alarmApi.removeRecipient(Number(alarm.id) as Dryp.AlarmId, currentUserId as Dryp.UserId);
						modalStore.close();
						refresh();
					} catch {
						modalStore.close();
					}
				},
			});
		},
		[currentUserId, modalStore, refresh],
	);

	const handleOpenAlarmWizard = useCallback(() => {
		modalStore.open("alarmWizard", { onSuccess: refresh });
	}, [modalStore, refresh]);

	const handleEdit = useCallback(
		(alarm: Alarm) => {
			modalStore.open("alarmWizard", { alarmId: alarm.id, onSuccess: refresh });
		},
		[modalStore, refresh],
	);

	// ── Table ────────────────────────────────────────────────────

	const columns = useMemo(
		() =>
			getAlarmsColumns({
				onAlarmToggled: silentRefresh,
				onEdit: handleEdit,
				onDelete: handleDelete,
				onViewLog: handleViewLog,
				onUnsubscribe: handleUnsubscribe,
				canManageMembers,
			}),
		[silentRefresh, handleEdit, handleDelete, handleViewLog, handleUnsubscribe, canManageMembers],
	);

	const { table } = useServerTable({
		columns,
		data: alarms,
		fetchFn: params =>
			alarmsApi.fetch(
				{ pageNumber: params.page, pageSize: params.pageSize },
				params.sort,
				getAlarmListFilters(params.filters, currentUserId),
			),
		initialValues: {
			page: 0,
			pageSize: 10,
		},
		enableUrlParams: true,
		initFromUrl: true,
	});

	const filterApi = useTableFilters(table);

	// ── Render ───────────────────────────────────────────────────

	return (
		<div className="flex flex-col gap-6 p-6 min-h-full">
			<header className="flex items-start justify-between gap-2">
				<div className="flex flex-col items-start gap-2">
					<h1 className="text-4xl font-semibold tracking-tight">Alarms</h1>
					<FeatureStatusBadge featureName="Alarms" status="research-preview" />
				</div>
				{canManageMembers && (
					<Button onClick={handleOpenAlarmWizard} size="sm">
						+ Create Alarm
					</Button>
				)}
			</header>

			<div className="flex flex-col gap-4 flex-1 min-h-0">
				<List
					table={table}
					pagination={{ total: alarmsApi.data?.query?.pagination.totalItems || 0 }}
					layout={{ loading: alarmsApi.loading }}
					toolbar={{
						filterKey: CUSTOM_FILTERS.search,
						customFilter: (
							<div className="flex items-center gap-2">
								<AlarmTypeFilter filter={filterApi} />
								<AlarmActiveFilter filter={filterApi} />
								<OwnAlarmsToggle filter={filterApi} />
							</div>
						),
						refreshFunction: refresh,
					}}
				/>
			</div>
		</div>
	);
});
